import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Form, Spinner } from "react-bootstrap";
import { Alert } from "@mui/material";
import {
  FaQrcode,
  FaSyncAlt,
  FaChartLine,
  FaGlobeAmericas,
  FaMobileAlt,
  FaDesktop,
  FaApple,
  FaAndroid,
  FaWindows,
  FaLinux,
  FaCalendarAlt,
  FaDatabase,
  FaPlus,
  FaTrash,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { getDatabase, ref, get, set } from "firebase/database";
import "./QRAnalytics.css";

// ============================================
// API & Cache Configuration
// ============================================
const CACHE_PREFIX = "qrtiger_cache_";
const CACHE_TTL_TRACK_DATA = 15 * 60 * 1000; // 15 minutes for tracking data
const QRTIGER_API_BASE = "https://api.qrtiger.com/api";
const apiKey = process.env.REACT_APP_QRTIGER_API_KEY;

// ============================================
// In-Memory Session Cache (Wiped on Refresh)
// ============================================
const sessionCache = {}; 

function getCachedData(key) {
  const cached = sessionCache[key];
  if (!cached) return null;
  if (cached.expiry && Date.now() > cached.expiry) {
    delete sessionCache[key];
    return null;
  }
  return cached;
}

function setCachedData(key, data, ttlMs) {
  sessionCache[key] = { 
    data, 
    timestamp: Date.now(), 
    expiry: Date.now() + ttlMs 
  };
}

function trackCacheKey(qrId, period) {
  return `track_${qrId}_${period}`;
}

function timeAgo(ts) {
  if (!ts) return "";
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} mins ago`;
  const hrs = Math.floor(mins / 60);
  return hrs === 1 ? "1 hour ago" : `${hrs} hours ago`;
}

// ============================================
// Main Component
// ============================================
export default function QRAnalytics() {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [qrCodes, setQrCodes] = useState([]); // [{id, name, url}] from Firebase
  const [selectedQR, setSelectedQR] = useState(null);
  const [trackData, setTrackData] = useState(null);
  const [period, setPeriod] = useState("months");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheInfo, setCacheInfo] = useState({ source: null, timestamp: null });

  // Add QR code form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQrId, setNewQrId] = useState("");
  const [newQrName, setNewQrName] = useState("");
  const [addingQR, setAddingQR] = useState(false);

  // --- Mount: load QR codes from Firebase ---
  useEffect(() => {
    loadQRCodesFromDB();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Load QR code IDs from Firebase ---
  const loadQRCodesFromDB = async () => {
    try {
      setLoading(true);
      const snapshot = await get(ref(getDatabase(), "qrTigerCodes"));
      const data = snapshot.val();
      const codes = data
        ? Object.entries(data).map(([key, val]) => ({
            firebaseKey: key,
            id: val.qrId || key,
            name: val.name || val.qrId || key,
          }))
        : [];
      setQrCodes(codes);

      // Auto-select first
      if (codes.length > 0) {
        setSelectedQR(codes[0]);
        await loadTrackData(codes[0].id, period, false);
      }
    } catch (err) {
      console.error("Error loading QR codes from Firebase:", err);
      setError("Failed to load QR codes from database");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // --- Add a QR code to Firebase ---
  const handleAddQRCode = async () => {
    if (!newQrId.trim()) return;
    try {
      setAddingQR(true);
      setError("");
      const firebaseKey = newQrId.trim().replace(/[.#$[\]]/g, "_");
      await set(ref(getDatabase(), `qrTigerCodes/${firebaseKey}`), {
        qrId: newQrId.trim(),
        name: newQrName.trim() || newQrId.trim(),
        addedAt: new Date().toISOString(),
      });
      setNewQrId("");
      setNewQrName("");
      setShowAddForm(false);

      // Reload from DB
      await loadQRCodesFromDB();
    } catch (err) {
      console.error("Error adding QR code:", err);
      setError("Failed to add QR code: " + err.message);
    } finally {
      setAddingQR(false);
    }
  };

  // --- Remove a QR code from Firebase ---
  const handleRemoveQRCode = async (firebaseKey) => {
    if (!window.confirm("Are you sure you want to remove this QR code from tracking?")) return;
    try {
      await set(ref(getDatabase(), `qrTigerCodes/${firebaseKey}`), null);
      // Clear from state
      const remaining = qrCodes.filter((qr) => qr.firebaseKey !== firebaseKey);
      setQrCodes(remaining);
      if (selectedQR?.firebaseKey === firebaseKey) {
        setSelectedQR(remaining[0] || null);
        if (remaining[0]) loadTrackData(remaining[0].id, period, false);
        else setTrackData(null);
      }
    } catch (err) {
      console.error("Error removing QR code:", err);
      setError("Failed to remove QR code: " + err.message);
    }
  };

  // --- Fetch Track Data from Backend Proxy ---
  const loadTrackData = async (qrId, selectedPeriod, forceRefresh) => {
    if (!qrId) return;
    try {
      setLoading(true);
      setError("");
      const cKey = trackCacheKey(qrId, selectedPeriod);

      // 1. Check cache
      if (!forceRefresh) {
        const cached = getCachedData(cKey);
        if (cached) {
          console.log(`[QR Analytics] Cache hit: ${qrId}/${selectedPeriod}`);
          setTrackData(cached.data);
          setCacheInfo({ source: "cache", timestamp: cached.timestamp });
          setLoading(false);
          setIsRefreshing(false);
          return;
        }
      }

      // 2. Fetch from QR TIGER API
      console.log(`[QR Analytics] API call: ${qrId}/${selectedPeriod}`);
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
      let url = `${QRTIGER_API_BASE}/data/${qrId}?period=${selectedPeriod}&tz=${tz}`;

      if (selectedPeriod === "day") {
        url += `&timestamp=${Math.floor(Date.now() / 1000)}`;
      }

      const res = await fetch(url, {
        headers: { 
          Authorization: `Bearer ${apiKey}`,
          Accept: "application/json" 
        },
      });
      if (!res.ok) throw new Error(`Failed to fetch tracking data (${res.status})`);

      const json = await res.json();
      const result = json?.data || json;

      setCachedData(cKey, result, CACHE_TTL_TRACK_DATA);
      setTrackData(result);
      setCacheInfo({ source: "api", timestamp: Date.now() });
    } catch (err) {
      console.error("Error fetching track data:", err);
      setError(err.message || "Failed to fetch tracking data. Make sure backend is running.");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // --- Handlers ---
  const handleQRSelect = (e) => {
    const qrId = e.target.value;
    const sel = qrCodes.find((qr) => qr.id === qrId);
    setSelectedQR(sel);
    if (sel) loadTrackData(qrId, period, false);
  };

  const handlePeriodChange = (e) => {
    const p = e.target.value;
    setPeriod(p);
    if (selectedQR) loadTrackData(selectedQR.id, p, false);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    if (selectedQR) {
      loadTrackData(selectedQR.id, period, true); // FORCE bypass cache
    }
  };

  // --- Analytics memoizing ---
  const analytics = useMemo(() => {
    const data = trackData || {};
    return {
      totalScans: data.scans || 0,
      uniqueScans: data.totalUniqueScan || 0,
      todayScans: data.campaign?.todayScan || 0,
      qrName: data.campaign?.qrName || "",
      scansByDate: data.graph || [],
      osData: data.device || [],
      cityData: data.city || [],
      countryData: data.country || [],
    };
  }, [trackData]);

  // --- Loading state ---
  if (initialLoading) {
    return (
      <div className="qr-analytics-loading">
        <div className="pulse-ring"></div>
        <p>Loading QR Analytics...</p>
      </div>
    );
  }

  // --- Main Dashboard ---
  return (
    <div className="qr-analytics-dashboard">
      {/* Page Header */}
      <div className="row">
        <div className="col">
          <div className="page-description d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h1>QR Code Analytics</h1>
              <p>Track and analyze your QR code scan performance</p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary d-flex align-items-center gap-1"
                style={{ borderRadius: "8px", fontSize: "0.8rem" }}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                <FaPlus size={10} /> Add QR Code
              </button>
              <a
                href="https://www.qrcode-tiger.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                style={{ borderRadius: "8px", fontSize: "0.8rem" }}
              >
                <FaExternalLinkAlt size={10} /> QR TIGER
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Add QR Code Form */}
      {showAddForm && (
        <div className="qr-add-form mb-4" style={{
          background: "#f8f9ff", border: "1px solid #e8edff", borderRadius: "12px", padding: "20px"
        }}>
          <h6 style={{ fontWeight: 600, marginBottom: "12px" }}>
            <FaPlus size={12} className="me-2" />Add QR Code for Tracking
          </h6>
          <p style={{ fontSize: "0.8rem", color: "#6c757d", marginBottom: "16px" }}>
            Enter the QR code ID from your QR TIGER dashboard. You can find it in the URL when viewing a QR code's details
            (e.g., the ID in <code>qrcode-tiger.com/data/YOUR_QR_ID</code>).
          </p>
          <div className="d-flex gap-2 flex-wrap">
            <Form.Control
              type="text"
              placeholder="QR Code ID (required)"
              value={newQrId}
              onChange={(e) => setNewQrId(e.target.value)}
              style={{ maxWidth: "300px", borderRadius: "8px", fontSize: "0.85rem" }}
            />
            <Form.Control
              type="text"
              placeholder="Display Name (optional)"
              value={newQrName}
              onChange={(e) => setNewQrName(e.target.value)}
              style={{ maxWidth: "250px", borderRadius: "8px", fontSize: "0.85rem" }}
            />
            <button
              className="btn btn-primary btn-sm"
              style={{ borderRadius: "8px", fontSize: "0.85rem", padding: "6px 20px" }}
              onClick={handleAddQRCode}
              disabled={!newQrId.trim() || addingQR}
            >
              {addingQR ? <Spinner size="sm" animation="border" /> : "Add"}
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ borderRadius: "8px", fontSize: "0.85rem" }}
              onClick={() => { setShowAddForm(false); setNewQrId(""); setNewQrName(""); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" className="mb-3" onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Controls Bar */}
      {qrCodes.length > 0 && (
        <Row className="mb-4">
          <Col>
            <div className="qr-controls-bar">
              <Form.Select
                style={{ maxWidth: "300px" }}
                onChange={handleQRSelect}
                value={selectedQR?.id || ""}
                disabled={loading || qrCodes.length === 0}
              >
                {qrCodes.map((qr) => (
                  <option key={qr.id} value={qr.id}>{qr.name}</option>
                ))}
              </Form.Select>

              <Form.Select
                style={{ maxWidth: "180px" }}
                value={period}
                onChange={handlePeriodChange}
                disabled={loading}
              >
                <option value="day">Today</option>
                <option value="days">All Days</option>
                <option value="months">All Months</option>
                <option value="year">This Year</option>
              </Form.Select>

              <button
                className={`qr-refresh-btn ${isRefreshing ? "spinning" : ""}`}
                onClick={handleRefresh}
                disabled={loading}
                title="Force refresh — bypasses cache and makes a fresh API call"
              >
                <FaSyncAlt size={14} />
                {loading ? "Loading..." : "Refresh"}
              </button>

              {/* Cache Status */}
              {cacheInfo.timestamp && (
                <span
                  className="d-flex align-items-center gap-1"
                  style={{ fontSize: "0.72rem", color: "#9ca3af", whiteSpace: "nowrap" }}
                  title={cacheInfo.source === "cache"
                    ? `Served from cache. Fetched ${timeAgo(cacheInfo.timestamp)}. Click Refresh for fresh data.`
                    : `Fresh data from API, fetched ${timeAgo(cacheInfo.timestamp)}.`}
                >
                  <FaDatabase size={10} />
                  {cacheInfo.source === "cache" ? <>Cached · {timeAgo(cacheInfo.timestamp)}</> : <>Live · {timeAgo(cacheInfo.timestamp)}</>}
                </span>
              )}
            </div>
          </Col>
        </Row>
      )}

      {/* Empty State — No QR codes added yet */}
      {qrCodes.length === 0 && !showAddForm && (
        <div className="qr-empty-state" style={{ padding: "80px 20px" }}>
          <div className="empty-icon" style={{ width: "100px", height: "100px", marginBottom: "20px" }}>
            <FaQrcode size={40} />
          </div>
          <h5>No QR Codes Added</h5>
          <p>Add your QR TIGER dynamic QR code IDs to start tracking their scan analytics.</p>
          <button
            className="btn btn-primary mt-3"
            style={{ borderRadius: "8px" }}
            onClick={() => setShowAddForm(true)}
          >
            <FaPlus size={12} className="me-2" />Add Your First QR Code
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && !initialLoading && (
        <div className="text-center mb-3">
          <Spinner animation="border" size="sm" variant="primary" />{" "}
          <span className="text-muted" style={{ fontSize: "0.85rem" }}>Fetching latest data...</span>
        </div>
      )}

      {/* Stat Cards — only show when we have a selected QR */}
      {selectedQR && (
        <>
          <Row className="mb-4">
            <Col xs={12} sm={6} lg={4} className="mb-3">
              <div className="qr-stat-card card h-100"><div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <div className="stat-icon-wrapper blue"><FaQrcode size={20} /></div>
                  <div><div className="stat-label">Total Scans</div><div className="stat-value">{analytics.totalScans.toLocaleString()}</div></div>
                </div>
              </div></div>
            </Col>
            <Col xs={12} sm={6} lg={4} className="mb-3">
              <div className="qr-stat-card card h-100"><div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <div className="stat-icon-wrapper orange"><FaGlobeAmericas size={20} /></div>
                  <div><div className="stat-label">Unique Scans</div><div className="stat-value">{analytics.uniqueScans.toLocaleString()}</div></div>
                </div>
              </div></div>
            </Col>
            <Col xs={12} sm={6} lg={4} className="mb-3">
              <div className="qr-stat-card card h-100"><div className="card-body">
                <div className="d-flex align-items-center gap-3">
                  <div className="stat-icon-wrapper green"><FaCalendarAlt size={20} /></div>
                  <div><div className="stat-label">Today's Scans</div><div className="stat-value">{analytics.todayScans.toLocaleString()}</div></div>
                </div>
              </div></div>
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="mb-4">
            <Col xs={12} lg={8} className="mb-3">
              <div className="qr-chart-card card h-100">
                <div className="card-header">
                  <h6><FaChartLine className="me-2 text-primary" size={14} />Scan Activity Over Time</h6>
                  <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                    {period === "day" ? "Hourly" : period === "days" ? "Daily" : "Monthly"} breakdown
                  </span>
                </div>
                <div className="card-body"><ScanBarChart data={analytics.scansByDate} /></div>
              </div>
            </Col>
            <Col xs={12} lg={4} className="mb-3">
              <div className="qr-chart-card card h-100">
                <div className="card-header"><h6><FaDesktop className="me-2 text-primary" size={14} />Device Breakdown</h6></div>
                <div className="card-body"><DeviceDonutChart data={analytics.osData} /></div>
              </div>
            </Col>
          </Row>

          {/* Location Row */}
          <Row className="mb-4">
            <Col xs={12} lg={6} className="mb-3">
              <div className="qr-chart-card card h-100">
                <div className="card-header"><h6><FaGlobeAmericas className="me-2 text-primary" size={14} />Top Scan Locations</h6></div>
                <div className="card-body p-0">
                  <LocationTable cityData={analytics.cityData} countryData={analytics.countryData} />
                </div>
              </div>
            </Col>
            <Col xs={12} lg={6} className="mb-3">
              <div className="qr-chart-card card h-100">
                <div className="card-header"><h6><FaQrcode className="me-2 text-primary" size={14} />QR Code Info</h6></div>
                <div className="card-body">
                  <QRCodeDetails qr={selectedQR} analytics={analytics} />
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}

      {/* All Tracked QR Codes */}
      {qrCodes.length > 0 && (
        <Row className="mb-4">
          <Col>
            <div className="qr-chart-card card">
              <div className="card-header">
                <h6><FaQrcode className="me-2 text-primary" size={14} />Manage Tracked QR Codes ({qrCodes.length})</h6>
              </div>
              <div className="card-body p-0">
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {qrCodes.map((qr) => {
                    const isActive = selectedQR?.id === qr.id;
                    return (
                      <div
                        key={qr.id}
                        className={`qr-code-item ${isActive ? "active" : ""}`}
                        onClick={() => { setSelectedQR(qr); loadTrackData(qr.id, period, false); }}
                      >
                        <div className="qr-icon"><FaQrcode size={16} /></div>
                        <div className="qr-info">
                          <div className="qr-name">{qr.name}</div>
                          <div className="qr-url">ID: {qr.id}</div>
                        </div>
                        <button
                          className="btn btn-sm"
                          style={{ color: "#ef4444", background: "transparent", border: "none" }}
                          onClick={(e) => { e.stopPropagation(); handleRemoveQRCode(qr.firebaseKey); }}
                          title="Remove from tracking"
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
}

// ============================================
// Sub-Components
// ============================================

function ScanBarChart({ data }) {
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => {
      let label = "";
      if (item._id) {
        if (item._id.month && item._id.year) {
          const m = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          label = `${m[item._id.month - 1]} ${item._id.year}`;
        } else if (item._id.day) {
          label = `${item._id.month}/${item._id.day}`;
        } else if (item._id.hour !== undefined) {
          label = `${item._id.hour}:00`;
        }
      }
      return {
        label: label || "Unknown",
        value: item.count || 0,
      };
    });
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div className="qr-empty-state" style={{ padding: "30px" }}>
        <div className="empty-icon" style={{ width: "60px", height: "60px" }}><FaChartLine size={24} /></div>
        <h5 style={{ fontSize: "0.95rem" }}>No Scan Data Yet</h5>
        <p style={{ fontSize: "0.8rem" }}>Scan data will appear here once your QR codes start getting scans.</p>
      </div>
    );
  }

  const maxValue = Math.max(...chartData.map((d) => d.value), 1);
  return (
    <div className="scan-bar-chart">
      {chartData.slice(-30).map((item, i) => {
        const height = Math.max((item.value / maxValue) * 180, 4);
        let displayLabel = item.label;
        if (displayLabel.length > 10) displayLabel = displayLabel.substring(0, 10);
        return (
          <div key={i} className="bar-wrapper">
            <div className="bar-value">{item.value > 0 ? item.value : ""}</div>
            <div className="bar" style={{ height: `${height}px` }} title={`${item.label}: ${item.value} scans`} />
            <div className="bar-label">{displayLabel}</div>
          </div>
        );
      })}
    </div>
  );
}

function DeviceDonutChart({ data }) {
  const osEntries = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item) => ({
      name: item._id?.device || "Unknown",
      value: item.count || 0,
    })).sort((a, b) => b.value - a.value);
  }, [data]);

  const colors = ["#4361ee", "#10b981", "#f59e0b", "#7c3aed", "#e11d48", "#0891b2"];
  const total = osEntries.reduce((acc, e) => acc + e.value, 0);

  const getOsIcon = (name) => {
    const l = name.toLowerCase();
    if (l.includes("ios") || l.includes("iphone") || l.includes("mac")) return <FaApple size={12} />;
    if (l.includes("android")) return <FaAndroid size={12} />;
    if (l.includes("windows")) return <FaWindows size={12} />;
    if (l.includes("linux")) return <FaLinux size={12} />;
    return <FaDesktop size={12} />;
  };

  if (osEntries.length === 0) {
    return (
      <div className="qr-empty-state" style={{ padding: "30px" }}>
        <div className="empty-icon" style={{ width: "60px", height: "60px" }}><FaDesktop size={24} /></div>
        <h5 style={{ fontSize: "0.95rem" }}>No Device Data</h5>
        <p style={{ fontSize: "0.8rem" }}>Device breakdown will appear once scans are recorded.</p>
      </div>
    );
  }

  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  let cumulativePercent = 0;

  return (
    <div className="donut-chart-container">
      <svg className="donut-chart-svg" viewBox="0 0 200 200">
        {osEntries.map((entry, i) => {
          const percent = total > 0 ? entry.value / total : 0;
          const dashArray = `${percent * circumference} ${circumference}`;
          const dashOffset = -cumulativePercent * circumference;
          cumulativePercent += percent;
          return (
            <circle key={i} cx="100" cy="100" r={radius} fill="none"
              stroke={colors[i % colors.length]} strokeWidth="24"
              strokeDasharray={dashArray} strokeDashoffset={dashOffset}
              transform="rotate(-90 100 100)" style={{ transition: "all 0.6s ease" }}
            />
          );
        })}
        <text x="100" y="95" textAnchor="middle" fontSize="24" fontWeight="700" fill="#1a1a2e">{total}</text>
        <text x="100" y="115" textAnchor="middle" fontSize="11" fill="#9ca3af">total</text>
      </svg>
      <div className="donut-legend">
        {osEntries.map((entry, i) => (
          <div key={i} className="donut-legend-item">
            <div className="donut-legend-color" style={{ background: colors[i % colors.length] }} />
            <span className="d-flex align-items-center gap-1">{getOsIcon(entry.name)} {entry.name}</span>
            <span className="donut-legend-value">
              {entry.value} <span style={{ color: "#9ca3af", fontWeight: 400 }}>({total > 0 ? Math.round((entry.value / total) * 100) : 0}%)</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LocationTable({ cityData, countryData }) {
  const locations = useMemo(() => {
    let entries = [];
    if (cityData && Array.isArray(cityData)) {
      entries = entries.concat(cityData.map(item => ({
        location: item._id?.city || "Unknown",
        type: "City",
        scans: item.count || 0
      })));
    }
    if (countryData && Array.isArray(countryData)) {
      entries = entries.concat(countryData.map(item => ({
        location: item._id?.loc || "Unknown",
        type: "Country",
        scans: item.count || 0
      })));
    }
    return entries.filter((e) => e.scans > 0).sort((a, b) => b.scans - a.scans).slice(0, 10);
  }, [cityData, countryData]);

  if (locations.length === 0) {
    return (
      <div className="qr-empty-state" style={{ padding: "30px" }}>
        <div className="empty-icon" style={{ width: "60px", height: "60px" }}><FaGlobeAmericas size={24} /></div>
        <h5 style={{ fontSize: "0.95rem" }}>No Location Data</h5>
        <p style={{ fontSize: "0.8rem" }}>Location data will appear once scans are recorded.</p>
      </div>
    );
  }

  const maxScans = Math.max(...locations.map((l) => l.scans), 1);
  return (
    <table className="location-table">
      <thead><tr><th style={{ width: "50px" }}>#</th><th>Location</th><th>Level</th><th>Scans</th><th style={{ width: "120px" }}></th></tr></thead>
      <tbody>
        {locations.map((loc, i) => (
          <tr key={i}>
            <td><span className={`rank-badge ${i === 0 ? "rank-1" : i === 1 ? "rank-2" : i === 2 ? "rank-3" : "rank-other"}`}>{i + 1}</span></td>
            <td style={{ fontWeight: 600 }}>{loc.location}</td>
            <td><span className="badge" style={{ background: loc.type === "City" ? "#eef2ff" : "#ecfdf5", color: loc.type === "City" ? "#4361ee" : "#10b981", fontWeight: 500, fontSize: "0.72rem" }}>{loc.type}</span></td>
            <td style={{ fontWeight: 700 }}>{loc.scans.toLocaleString()}</td>
            <td><div className="location-scan-bar"><div className="bar-fill" style={{ width: `${(loc.scans / maxScans) * 100}%` }} /></div></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function QRCodeDetails({ qr, analytics }) {
  const detailItems = [
    { label: "Display Name", value: qr?.name || "—" },
    { label: "QR Code ID", value: <code style={{ background: "#f3f4f6", padding: "2px 8px", borderRadius: "4px", fontSize: "0.75rem", color: "#6b7280" }}>{qr?.id}</code> },
    { label: "QR Title (from API)", value: analytics.qrName || "—" },
    { label: "Total Scans", value: analytics.totalScans.toLocaleString() },
    { label: "Today's Scans", value: analytics.todayScans.toLocaleString() },
    { label: "Unique Scans", value: analytics.uniqueScans.toLocaleString() },
  ];

  return (
    <div>
      {detailItems.map((item, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < detailItems.length - 1 ? "1px solid #f5f5f5" : "none" }}>
          <span style={{ fontSize: "0.82rem", color: "#6c757d" }}>{item.label}</span>
          <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#1a1a2e", textAlign: "right" }}>{item.value}</span>
        </div>
      ))}
    </div>
  );
}
